import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Shield, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { LoginForm } from './auth/LoginForm';
import { useAuth } from '@/components/auth/AuthContext';
import { useApp } from '@/pages/providers/AppProvider';

interface SuperAdminLoginProps {
  onLogin: (user: any) => void;
  onBackToWebsite: () => void;
}

export function SuperAdminLogin({ onLogin, onBackToWebsite }: SuperAdminLoginProps) {
  const { user } = useAuth();
  const { siteSettings } = useApp();
  const establishedYear = siteSettings?.establishedYear || String(new Date().getFullYear());
  const companyName = siteSettings?.companyName || 'MegaJobNepal';

  const handleLoginSuccess = (authUser?: any) => {
    const effectiveUser = authUser || user;
    if (effectiveUser) {
      const role = String(effectiveUser.user_type || '').toLowerCase();
      onLogin(effectiveUser);
      if (role === 'super_admin') {
        toast.success('Welcome to Super Admin Panel!');
      }
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 flex items-center justify-center p-6 overflow-hidden animate-in fade-in duration-500">
      <div className="absolute -top-24 -left-24 h-64 w-64 bg-gradient-to-br from-purple-300 to-indigo-300 rounded-full blur-3xl opacity-40 animate-pulse" />
      <div className="absolute -bottom-24 -right-24 h-72 w-72 bg-gradient-to-br from-indigo-300 to-purple-300 rounded-full blur-3xl opacity-30 animate-pulse" />

      <div className="w-full max-w-md animate-in slide-in-from-bottom-4 duration-700">
        <Button
          variant="ghost"
          onClick={onBackToWebsite}
          className="mb-6 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Website
        </Button>

        <Card className="shadow-2xl border-0 bg-white/90 backdrop-blur-sm transition-transform duration-300 hover:scale-[1.01]">
          <CardHeader className="text-center pb-2 animate-in fade-in slide-in-from-top-2 duration-700">
            <div className="mx-auto w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mb-4 shadow-lg animate-in zoom-in-95 duration-500">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-800">Super Admin Login</CardTitle>
            <p className="text-gray-600 mt-2">Access the {companyName} Super Admin Panel</p>
          </CardHeader>
          <CardContent className="pt-2 animate-in fade-in slide-in-from-bottom-2 duration-700">
            <div className="space-y-4">
              <LoginForm isAdminLogin={true} onSuccess={handleLoginSuccess} />
            </div>
          </CardContent>
        </Card>

        <div className="mt-6 text-center animate-in fade-in duration-700">
          <p className="text-sm text-gray-500">© {establishedYear} {companyName.endsWith('.') ? companyName.slice(0, -1) : companyName}. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
